import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import apiClient from "../apiClient";
import { formatMoney } from "../formatMoney";
import { formatNumber } from "../formatNumber";

const ReportsPassiveIncome = () => {
  const [report, setReport] = useState({ annualIncome: 0, monthlyIncome: 0, rows: [] });
  const [defaultCurrency, setDefaultCurrency] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchReport = async () => {
      try {
        const [reportRes, currenciesRes] = await Promise.all([
          apiClient.get("/reports/passive-income"),
          apiClient.get("/currencies"),
        ]);
        const currencyRows = Array.isArray(currenciesRes.data) ? currenciesRes.data : [];
        setReport({
          annualIncome: Number(reportRes.data?.annualIncome || 0),
          monthlyIncome: Number(reportRes.data?.monthlyIncome || 0),
          rows: Array.isArray(reportRes.data?.rows) ? reportRes.data.rows : [],
        });
        setDefaultCurrency(currencyRows.find((row) => Number(row.IsDefault))?.Description || "");
      } catch (err) {
        setError("Failed to load passive income report.");
        setReport({ annualIncome: 0, monthlyIncome: 0, rows: [] });
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, []);

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">Passive Income</h1>
          <p className="page-subtitle">
            <Link to="/">Home</Link> / <Link to="/Reports">Reports</Link> / Passive Income
          </p>
        </div>
      </header>

      {error && <p className="notice notice-error">{error}</p>}
      {loading && <p className="notice notice-muted">Loading passive income report...</p>}

      {!loading && (
        <>
          <section className="panel">
            <p className="notice notice-muted report-context-note">
              Totals and converted balances are shown in default currency: {defaultCurrency || "Default"}
            </p>

            <div className="dashboard-grid report-summary-strip">
              <div className="metric-card metric-card-compact">
                <strong>Annual Income</strong>
                <div className="resource-meta">{formatMoney(report.annualIncome, defaultCurrency || "Default")}</div>
              </div>
              <div className="metric-card metric-card-compact">
                <strong>Monthly Income</strong>
                <div className="resource-meta">{formatMoney(report.monthlyIncome, defaultCurrency || "Default")}</div>
              </div>
              <div className="metric-card metric-card-compact">
                <strong>Interest-Bearing Banks</strong>
                <div className="resource-meta">{formatNumber(report.rows.length).replace(".00", "")}</div>
              </div>
            </div>
          </section>

          <section className="resource-list" style={{ marginTop: 20 }}>
            {report.rows.length === 0 ? (
              <p className="notice notice-muted">No interest-bearing bank balances found.</p>
            ) : (
              report.rows.map((row) => (
                <article key={row.BankID} className="resource-row">
                  <div>
                    <strong>{row.BankDescription}</strong>
                    <div className="resource-meta">Interest rate: {Number(row.AnnualInterestRate || 0).toFixed(2)}%</div>
                    <div className="resource-meta">Latest balance: {formatMoney(row.BalanceAmount, row.CurrencyDescription)}</div>
                    <div className="resource-meta">Balance date: {row.BalanceDate || "-"}</div>
                    <div className="resource-meta">Balance in default currency: {formatMoney(row.BalanceInDefaultCurrency, defaultCurrency || "Default")}</div>
                  </div>
                  <div>
                    <strong>{formatMoney(row.AnnualPassiveIncome, defaultCurrency || "Default")}</strong>
                    <div className="resource-meta">Annual passive income</div>
                    <div className="resource-meta">Monthly: {formatMoney(row.MonthlyPassiveIncome, defaultCurrency || "Default")}</div>
                  </div>
                </article>
              ))
            )}
          </section>
        </>
      )}
    </div>
  );
};

export default ReportsPassiveIncome;
